import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge, Row, Col, ListGroup } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import { getValidAccessToken } from '../utils/tokenManager';
import { formatDate } from '../utils/dateFormatter';

const EXPORT_LABELS = {
  customers: 'Customers',
  transactions: 'Transactions',
  payments: 'Payments',
  vehicles: 'Vehicles',
  rto: 'RTO Agreements',
};

function StatusBadge({ status }) {
  const variants = {
    pending: 'warning',
    processing: 'info',
    completed: 'success',
    failed: 'danger',
    expired: 'secondary',
  };
  return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
}

export function AuditExport() {
  const { user } = useOutletContext();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});

  const authHeader = useCallback(async () => {
    const token = await getValidAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const headers = await authHeader();
      const res = await fetch(`${API_BASE_URL}/export/jobs/latest`, { headers });
      const data = await res.json();
      if (data.success) {
        setJob(data.job);
        setError(null);
      }
    } catch {
      // silently ignore polling errors
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll while pending/processing
  useEffect(() => {
    if (!job || !['pending', 'processing'].includes(job.status)) return;
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [job, fetchStatus]);

  const triggerExport = async () => {
    setTriggering(true);
    setError(null);
    try {
      const headers = await authHeader();
      const res = await fetch(`${API_BASE_URL}/export/jobs`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        setJob(data.job);
      } else {
        setError(data.error || 'Failed to start export');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setTriggering(false);
    }
  };

  const downloadFile = async (type) => {
    setDownloading(prev => ({ ...prev, [type]: true }));
    try {
      const headers = await authHeader();
      const res = await fetch(
        `${API_BASE_URL}/export/jobs/${job.id}/download?type=${type}`,
        { headers }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Download failed');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [type]: false }));
    }
  };

  const isActive = job && ['pending', 'processing'].includes(job.status);
  const isReady = job && job.status === 'completed';
  const canTrigger = !job || ['failed', 'expired'].includes(job.status) || job.status === 'completed';

  return (
    <div>
      <h5 className="mb-1">Data Export</h5>
      <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
        Export all store data as CSV files for QuickBooks Enterprise reconciliation and auditing.
        Exports include customers, transactions, payments, vehicles, and RTO agreements.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" /> Loading...
        </div>
      ) : (
        <>
          {/* Trigger / Status Card */}
          <Card className="mb-4">
            <Card.Body>
              <Row className="align-items-center">
                <Col>
                  {!job || canTrigger ? (
                    <div>
                      <div className="fw-semibold">Ready to export</div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Generates fresh CSV files for all data in your accessible stores.
                        {isReady && ' Previous export is available below.'}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="fw-semibold d-flex align-items-center gap-2">
                        Export in progress <StatusBadge status={job.status} />
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Started {job.started_at
                          ? new Date(job.started_at).toLocaleTimeString()
                          : 'just now'}. This usually takes under a minute.
                      </div>
                    </div>
                  )}
                </Col>
                <Col xs="auto">
                  {isActive ? (
                    <Button variant="outline-secondary" size="sm" onClick={fetchStatus}>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Checking...
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={triggerExport}
                      disabled={triggering}
                    >
                      {triggering ? (
                        <><Spinner animation="border" size="sm" className="me-1" /> Starting...</>
                      ) : (
                        isReady ? 'Generate New Export' : 'Generate Export'
                      )}
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Download Section */}
          {isReady && (
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Download Files</span>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Generated {new Date(job.completed_at).toLocaleString()} &middot; expires {formatDate(job.expires_at)}
                </div>
              </Card.Header>
              <ListGroup variant="flush">
                {Object.entries(EXPORT_LABELS).map(([type, label]) => (
                  <ListGroup.Item key={type} className="d-flex justify-content-between align-items-center">
                    <div>
                      <span className="fw-medium">{label}</span>
                      <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>CSV</span>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => downloadFile(type)}
                      disabled={!!downloading[type]}
                    >
                      {downloading[type] ? (
                        <><Spinner animation="border" size="sm" className="me-1" />Downloading...</>
                      ) : (
                        'Download'
                      )}
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card>
          )}

          {job && job.status === 'failed' && (
            <Alert variant="warning">
              Last export failed: {job.error_message || 'Unknown error'}. Please try again.
            </Alert>
          )}
        </>
      )}
    </div>
  );
}

export default AuditExport;
